import { z } from 'zod';
import { CrmCompanyStatusSchema, CrmCompanyTypeSchema } from './company-enums';
import { refersTo } from '../common/refers-to';

// The ContactCompanyRole join (IMM-4.3): a typed link between a contact and a
// CRM company carrying a free-text role and a "primärer Arbeitgeber" flag.

export const COMPANY_LINK_ROLE_MAX_LENGTH = 200;

export const CompanyLinkRoleSchema = z.string().trim().min(1).max(COMPANY_LINK_ROLE_MAX_LENGTH);

// Inline contact (MODE B) — IMM-3's minimal set: first + last name required,
// e-mail + phone optional.
const InlineContactSchema = z.object({
  firstName: z.string().trim().min(1).max(120),
  lastName: z.string().trim().min(1).max(120),
  email: z.email().optional(),
  phone: z.string().trim().min(1).max(32).optional(),
});

// Link a contact to a company — MODE A (existing contact) or MODE B (inline new
// contact). Exactly one of `contactId` / `contact` is set.
export const LinkContactToCompanyBodySchema = z
  .object({
    contactId: z.uuid().optional(),
    contact: InlineContactSchema.optional(),
    role: CompanyLinkRoleSchema,
    isPrimaryEmployer: z.boolean().default(false),
  })
  .refine((v) => (v.contactId === undefined) !== (v.contact === undefined), {
    message: 'Provide exactly one of contactId (existing) or contact (inline new contact)',
  });
export type LinkContactToCompanyBody = z.infer<typeof LinkContactToCompanyBodySchema>;

// The optional contact-link block folded into create-company for the atomic
// "capture a firm with its first contact" flow.
export const CreateCompanyContactLinkSchema = LinkContactToCompanyBodySchema;
export type CreateCompanyContactLink = z.infer<typeof CreateCompanyContactLinkSchema>;

export const UpdateCompanyLinkBodySchema = z.object({
  role: CompanyLinkRoleSchema,
  isPrimaryEmployer: z.boolean(),
});
export type UpdateCompanyLinkBody = z.infer<typeof UpdateCompanyLinkBodySchema>;

export const CompanyContactLinkResponseSchema = z.object({
  id: z.uuid(),
  contactId: refersTo('contact', z.uuid()),
  companyId: refersTo('company', z.uuid()),
  role: z.string(),
  isPrimaryEmployer: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type CompanyContactLinkResponse = z.infer<typeof CompanyContactLinkResponseSchema>;

// Company-side roster row: the linked contact's projection + the link fields.
export const CompanyContactRosterItemSchema = z.object({
  contactId: refersTo('contact', z.uuid()),
  displayName: z.string(),
  contactType: z.enum(['PERSON', 'COMPANY']),
  primaryEmail: z.email().nullable(),
  primaryPhone: z.string().nullable(),
  city: z.string().nullable(),
  role: z.string(),
  isPrimaryEmployer: z.boolean(),
});
export type CompanyContactRosterItem = z.infer<typeof CompanyContactRosterItemSchema>;

// Contact-side "Firmen" row: the linked company's projection + the link fields.
// `status` lets the frontend render the "(archiviert)" suffix without a second fetch.
export const ContactCompanyLinkItemSchema = z.object({
  companyId: refersTo('company', z.uuid()),
  name: z.string(),
  type: CrmCompanyTypeSchema.nullable(),
  status: CrmCompanyStatusSchema,
  role: z.string(),
  isPrimaryEmployer: z.boolean(),
});
export type ContactCompanyLinkItem = z.infer<typeof ContactCompanyLinkItemSchema>;

export const LinkContactToCompanyPathParamsSchema = z.object({
  organizationId: z.uuid(),
  companyId: z.uuid(),
});

export const CompanyLinkPathParamsSchema = z.object({
  organizationId: z.uuid(),
  companyId: z.uuid(),
  contactId: z.uuid(),
});

export const ListCompanyContactsPathParamsSchema = LinkContactToCompanyPathParamsSchema;
export const ListCompanyContactsResponseSchema = z.object({ data: z.array(CompanyContactRosterItemSchema) });
export type ListCompanyContactsResponse = z.infer<typeof ListCompanyContactsResponseSchema>;

export const ListContactCompaniesPathParamsSchema = z.object({
  organizationId: z.uuid(),
  contactId: z.uuid(),
});
export const ListContactCompaniesResponseSchema = z.object({ data: z.array(ContactCompanyLinkItemSchema) });
export type ListContactCompaniesResponse = z.infer<typeof ListContactCompaniesResponseSchema>;

export const UnlinkContactFromCompanyResponseSchema = z.object({
  contactId: refersTo('contact', z.uuid()),
  companyId: refersTo('company', z.uuid()),
});
export type UnlinkContactFromCompanyResponse = z.infer<typeof UnlinkContactFromCompanyResponseSchema>;
